import type {
  BurstChamber,
  ChartAudio,
  ChartPalette,
  FlipPad,
  Hazard,
  PulseChart,
  SectionMarker,
} from '../../chart.js';
import type { Difficulty, Surface, Variant } from '../../gameTypes.js';
import type { ReplayInputs } from '../../replay.js';

export interface ChartBuilderInit {
  id: string;
  slug: string;
  title: string;
  description: string;
  bpm: number;
  difficulty: Difficulty;
  /**
   * Per-track density variant — see {@link PulseChart.variant}. Builtin
   * charts produce three of these per track; user maps default to
   * `'normal'`.
   */
  variant: Variant;
  spawnSurface: Surface;
  palette: ChartPalette;
  audio: ChartAudio;
}

export class ChartBuilder {
  private readonly init: ChartBuilderInit;
  private pads: FlipPad[] = [];
  private hazards: Hazard[] = [];
  private sections: SectionMarker[] = [];
  private burstChambers: BurstChamber[] = [];
  private padCounter = 0;
  private hazardCounter = 0;
  private burstCounter = 0;
  private currentSurface: Surface;
  private endBeat = 0;
  private explicitTotalBeats: number | null = null;

  constructor(init: ChartBuilderInit) {
    this.init = init;
    this.currentSurface = init.spawnSurface;
  }

  pad(
    beat: number,
    opts: { surface?: Surface; optional?: boolean } = {},
  ): this {
    const surface =
      opts.surface ?? (this.currentSurface === 'floor' ? 'ceiling' : 'floor');
    const id = `pad-${this.padCounter++}`;
    const pad: FlipPad = { id, beat, surface };
    if (opts.optional) {
      pad.optional = true;
    }
    this.pads.push(pad);
    if (!opts.optional) {
      this.currentSurface = surface;
    }
    if (beat > this.endBeat) {
      this.endBeat = beat;
    }
    return this;
  }

  flipsAtBeats(beats: number[], opts: { optional?: boolean } = {}): this {
    for (const b of beats) {
      this.pad(b, opts);
    }
    return this;
  }

  bonusPad(beat: number, opts: { surface?: Surface } = {}): this {
    return this.pad(beat, { ...opts, optional: true });
  }

  hazard(opts: {
    startBeat: number;
    endBeat: number;
    surface: Surface;
    kind?: 'spike' | 'laser' | 'wall';
  }): this {
    const id = `hazard-${this.hazardCounter++}`;
    this.hazards.push({
      id,
      startBeat: opts.startBeat,
      endBeat: opts.endBeat,
      surface: opts.surface,
      kind: opts.kind ?? 'spike',
    });
    if (opts.endBeat > this.endBeat) {
      this.endBeat = opts.endBeat;
    }
    return this;
  }

  section(marker: SectionMarker): this {
    this.sections.push(marker);
    if (marker.endBeat > this.endBeat) {
      this.endBeat = marker.endBeat;
    }
    return this;
  }

  /**
   * Place a "MASH!" burst chamber spanning [startBeat, endBeat]. Inside
   * the chamber every tap is free score (no combo, no HP penalty); see
   * {@link BurstChamber} for the full contract.
   */
  burstChamber(opts: { startBeat: number; endBeat: number }): this {
    const id = `burst-${this.burstCounter++}`;
    this.burstChambers.push({
      id,
      startBeat: opts.startBeat,
      endBeat: opts.endBeat,
    });
    if (opts.endBeat > this.endBeat) {
      this.endBeat = opts.endBeat;
    }
    return this;
  }

  extendTo(beat: number): this {
    if (beat > this.endBeat) {
      this.endBeat = beat;
    }
    return this;
  }

  /**
   * Pin the chart's totalBeats to an exact value. When set, build() returns
   * exactly this many beats instead of `endBeat + 4`. Caller is responsible
   * for ensuring all pads/sections fit within `beats`.
   */
  setTotalBeats(beats: number): this {
    this.explicitTotalBeats = beats;
    return this;
  }

  build(): PulseChart {
    const totalBeats =
      this.explicitTotalBeats !== null
        ? Math.max(this.explicitTotalBeats, 8)
        : Math.max(this.endBeat + 4, 8);
    return {
      mode: 'pulse',
      id: this.init.id,
      slug: this.init.slug,
      title: this.init.title,
      description: this.init.description,
      bpm: this.init.bpm,
      difficulty: this.init.difficulty,
      variant: this.init.variant,
      totalBeats,
      spawnSurface: this.init.spawnSurface,
      palette: this.init.palette,
      pads: [...this.pads].sort((a, b) => a.beat - b.beat),
      hazards: [...this.hazards].sort((a, b) => a.startBeat - b.startBeat),
      sections: [...this.sections].sort((a, b) => a.startBeat - b.startBeat),
      burstChambers: [...this.burstChambers].sort(
        (a, b) => a.startBeat - b.startBeat,
      ),
      audio: this.init.audio,
    };
  }
}

export function expectedReplayFromPads(
  chart: PulseChart,
  options: { includeOptional?: boolean } = {},
): ReplayInputs {
  const includeOptional = options.includeOptional ?? false;
  const inputs = chart.pads
    .filter((p) => includeOptional || !p.optional)
    .map((p) => ({ beat: p.beat, type: 'flipDown' as const }))
    .sort((a, b) => a.beat - b.beat);
  return { loops: [{ inputs }] };
}
